import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { getIsLogin } from 'lowcode/utils/getIsLogin';
import { signupCookie } from '@/utils/cookies/signupCookie';
import gtm from '@/utils/gtm';
import { buildLoginRedirectUrl } from '../utils';
import { TranslationFn } from '../types';

interface UseLoginGuardProps {
    locale: string;
    title?: string;
    id?: string;
    loginType?: 'page' | 'modal';
    t: TranslationFn;
}

interface UseLoginGuardReturn {
    isLogin: boolean;
    showLoginModal: boolean;
    setShowLoginModal: (show: boolean) => void;
    isClient: boolean;
    /**
     * Guard function that checks login status and handles redirect/modal
     * Returns true if user is logged in, false otherwise
     */
    guardLogin: (entryPosition?: number, buttonName?: string) => boolean;
}

/**
 * Hook for handling login guard logic
 * Manages login state, login modal, and login redirect
 */
export const useLoginGuard = ({ locale, title, id, loginType = 'page', t }: UseLoginGuardProps): UseLoginGuardReturn => {
    const router = useRouter();
    const [isLogin, setIsLogin] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // Check login status on mount
    useEffect(() => {
        setIsLogin(getIsLogin());
    }, []);

    // Client-side check
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    /**
     * Guard function that checks login status
     * If not logged in, triggers signup cookie, GTM event, and redirect/modal
     */
    const guardLogin = useCallback(
        (entryPosition = 0, buttonName = 'Redeem Your EcoCredits_REDEEM NOW'): boolean => {
            if (isLogin) return true;

            // Track signup cookie
            signupCookie?.({
                sector_name: title || t('redeem_ecocredits_title'),
                entry_position: entryPosition,
            });

            if (loginType === 'modal') {
                // Push GTM event
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'activity_join_login',
                    button_name: buttonName,
                });
                setShowLoginModal(true);
                return false;
            }

            // Redirect to login page
            router.push(buildLoginRedirectUrl(locale, router.asPath, id));
            return false;
        },
        [isLogin, locale, title, id, loginType, t, router],
    );

    return {
        isLogin,
        showLoginModal,
        setShowLoginModal,
        isClient,
        guardLogin,
    };
};
